import { z } from 'zod';
import { BaseSignalDtoSchema } from '@terra/shared-contracts-signals';
import {
  SkipAttackReasonTypeSchema,
  type SkipAttackReasonType,
} from '@terra/shared-contracts-trace';
import {
  BlockedReasonSchema,
  SignalStageSchema,
  SignalStatusSchema,
} from '@terra/shared-schemas-orchestrator';
import { TraceFileSchema, TraceStatusSchema } from '@terra/shared-schemas-ai';
import { BaseArtifactDtoSchema } from '@terra/shared-contracts-artifact';

export { SkipAttackReasonTypeSchema };
export type { SkipAttackReasonType };

export const TodoItemSchema = z.object({
  id: z.string(),
  content: z.string(),
  status: z.union([
    z.literal('pending'),
    z.literal('completed'),
    z.literal('in_progress'),
    z.literal('cancelled'),
  ]),
});

export const TraceMetadataLabelSchema = z.enum([
  'EXPLOITED',
  'POTENTIAL_SIGNAL',
  'SAFE',
  'UNPROVEN',
  'CONFIRMED_VULNERABILITY',
  'SUSPECTED_VULNERABILITY',
  'NOT_OBSERVED (NO VULNERABILITY FOUND)',
  'BLOCKED',
]);

export type TraceMetadataLabel = z.infer<typeof TraceMetadataLabelSchema>;

export const TraceEvidenceSchema = z.object({
  explanation: z.string(),
  content: z.string().optional(),
  data: z.string().optional(),
  type: z.string().optional(),
  artifactId: z.string().optional(),
  toolExecutionHarId: z.string().nullable().optional(),
  evidenceId: z.string().optional(),
  evidenceType: z.string().optional(),
});

export const PostAttackBlockReasonSchema = BlockedReasonSchema;

export type PostAttackBlockReason = z.infer<typeof PostAttackBlockReasonSchema>;

export const TraceMetadataSchema = z.object({
  summary: z.string().optional(),
  attackLabel: TraceMetadataLabelSchema.optional(),
  labelRationale: z.string().optional(),
  confidenceReason: z.string().optional(),
  confidence: z.enum(['low', 'medium', 'high']).optional(),
  analysis: z.string().optional(),
  conclusion: z.string().optional(),
  blockReason: PostAttackBlockReasonSchema.optional(),
  ambiguityFactor: z.string().optional(),
  matchedRuleIds: z.array(z.string()).optional(),
  evidences: z.array(TraceEvidenceSchema).optional(),
  previousStage: SignalStageSchema.optional(),
  previousStatus: SignalStatusSchema.nullable().optional(),
  newStage: SignalStageSchema.optional(),
  newStatus: SignalStatusSchema.nullable().optional(),
  skipAttackReason: z.string().optional(),
  skipAttackReasonType: SkipAttackReasonTypeSchema,
});

export const StoryTraceStepSourceSchema = z.enum(['neo', 'co-pilot']);

export const StoryTraceStepSummarySchema = z.object({
  stepKey: z.string(),
  order: z.int(),
  source: StoryTraceStepSourceSchema,
  kind: z.string(),
  title: z.string(),
  visible: z.boolean(),
});

export const StoryTraceStepVisibilityBodySchema = z.object({
  visible: z.boolean(),
});

export const SmokeTestHistoryResponseSchema = z.object({
  threadId: z.string(),
  postAttackTraceId: z.string().nullable().optional(),
  signalId: z.string().nullable().optional(),
  signal: BaseSignalDtoSchema.nullable().optional(),
  isStoryTrace: z.boolean().optional(),
  plan: z.array(TodoItemSchema).optional(),
  steps: z.array(StoryTraceStepSummarySchema).optional(),
  messages: z.array(z.unknown()).optional(),
  files: z.array(TraceFileSchema.nullable()).optional(),
  artifacts: z.array(BaseArtifactDtoSchema).optional(),
  status: TraceStatusSchema.optional(),
  metadata: TraceMetadataSchema.nullable().optional(),
  postAttackMessages: z.array(z.unknown()).optional(),
});
